import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/i18n";
import { Loader2, AlertTriangle, Mail, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function AuthMagic() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const [state, setState] = useState("verifying"); // verifying | error | resend_sent
  const [errorMsg, setErrorMsg] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const token = params.get("token");
    const next = params.get("next") || "/dashboard";
    // Only allow same-site relative paths starting with `/dashboard` to avoid
    // open-redirect abuse — anything else falls back to the plain dashboard.
    const safeNext = (next.startsWith("/dashboard") && !next.startsWith("//")) ? next : "/dashboard";
    if (!token) {
      setErrorMsg(t("error_generic"));
      setState("error");
      return;
    }
    (async () => {
      try {
        await api.get(`/auth/magic-link/verify?token=${encodeURIComponent(token)}`);
        await refresh();
        // Protected + needsOnboarding gates still apply — if the student hasn't
        // accepted Terms & Conditions yet, they'll be sent to /onboarding first
        // and then can navigate to `safeNext` themselves after completing it.
        navigate(safeNext, { replace: true });
      } catch (e) {
        setErrorMsg(e?.response?.data?.detail || t("error_generic"));
        setState("error");
      }
    })();
  }, []);

  const requestNewLink = async (e) => {
    e?.preventDefault();
    if (!email) return;
    try {
      setSending(true);
      await api.post("/auth/magic-link/request", { email });
      setState("resend_sent");
    } catch {
      setErrorMsg(t("error_generic"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <div className="bg-white border border-slate-200 rounded-xl p-8 shadow-sm max-w-md w-full text-center">
        {state === "verifying" && (
          <>
            <Loader2 className="h-8 w-8 text-brand-blue animate-spin mx-auto" />
            <h1 className="font-display text-xl font-bold text-slate-900 mt-4">{t("loading")}</h1>
          </>
        )}

        {state === "error" && (
          <>
            <AlertTriangle className="h-8 w-8 text-amber-500 mx-auto" />
            <h1 className="font-display text-xl font-bold text-slate-900 mt-4">
              {t("magic_link_expired_title")}
            </h1>
            <p className="text-sm text-slate-600 mt-2">{errorMsg}</p>
            <p className="text-sm text-slate-600 mt-4">
              {t("magic_link_expired_hint")}
            </p>

            <form onSubmit={requestNewLink} className="mt-5 space-y-3 text-left">
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-500 mb-1.5 block">
                  {t("email")}
                </label>
                <Input
                  type="email"
                  data-testid="magic-resend-email-input"
                  placeholder="tu@email.com"
                  value={email}
                  onChange={(ev) => setEmail(ev.target.value)}
                  required
                />
              </div>
              <Button
                type="submit"
                data-testid="magic-resend-button"
                disabled={sending || !email}
                className="w-full btn-brand"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                {t("magic_link_send")}
              </Button>
            </form>

            <button
              onClick={() => navigate("/login", { replace: true })}
              className="mt-4 text-xs text-slate-500 hover:text-slate-900 underline"
              data-testid="back-to-login-button"
            >
              {t("login")}
            </button>
          </>
        )}

        {state === "resend_sent" && (
          <>
            <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto" />
            <h1 className="font-display text-xl font-bold text-slate-900 mt-4">
              {t("magic_link_sent_title")}
            </h1>
            <p className="text-sm text-slate-600 mt-2">{t("magic_link_check_email")}</p>
            <button
              onClick={() => navigate("/login", { replace: true })}
              className="mt-5 text-xs text-slate-500 hover:text-slate-900 underline"
              data-testid="back-to-login-button"
            >
              {t("login")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
